// * Session coordinator — the sequence + effects layer for client-side auth. It owns the
//   refresh single-flight, cross-tab adoption, retry-once, 401 recovery, and the collapse
//   trigger; it performs effects through the identity store and decides through sessionPolicy.
//
//   Deliberately free of HTTP mechanics (the axios interceptor owns retries/keys/redirects)
//   and of a runtime store import (type-only + explicit param) — this is what removes the
//   former identity <-> sessionRefresh runtime cycle.
//
//   Result contract (unchanged): on success the tokens are ALSO stored on the identity store
//   and returned; on failure `accessToken` is empty and `failedWith` carries the HTTP status
//   that failed the refresh (null for network/unknown). Callers collapse ONLY on a definitive
//   auth failure (401/403) — see sessionPolicy.

import type { useIdentityStore } from "@/stores/identity";

import { useLogger } from "@blackfisk/vue/composables/useLogger";

import { sessionRefresh } from "@/services/blackfisk/auth";
import {
  classifyUnauthorized,
  decideRefreshFailure,
  gateRefresh,
  isRefreshUrl,
} from "@/services/blackfisk/sessionPolicy";

type IdentityStore = ReturnType<typeof useIdentityStore>;

const logger = useLogger("client-frontend", "session-coordinator");

export type RefreshAccessTokenResult = {
  accessToken: string;
  csrfToken: string;
  expiresIn: number;
  failedWith?: number | null;
} | null;

// * Single-flight access-token refresh. Concurrent 401s (parallel requests at token expiry,
//   page load) must share ONE refresh — otherwise the losers would each burn the shared
//   cookie and drain the per-user refresh budget.
let inflight: ReturnType<typeof ensureSession> | null = null;

const doRefresh = async (): Promise<RefreshAccessTokenResult> => {
  const { errors, session } = await sessionRefresh();
  if (session?.accessToken) {
    return {
      accessToken: session.accessToken,
      csrfToken: session.csrfToken,
      expiresIn: session.expiresIn,
    };
  }
  const failed = errors.find((e) => typeof e.code === "number");
  const failedWith = typeof failed?.code === "number" ? failed.code : null;
  return { accessToken: "", csrfToken: "", expiresIn: 0, failedWith };
};

// * Ensure a usable session: adopt a sibling's recent token, else single-flight refresh,
//   else retry once if a sibling rotated the cookie while we were in flight.
export function ensureSession(store: IdentityStore): Promise<RefreshAccessTokenResult> {
  if (inflight) {
    return inflight;
  }
  // * Assign FIRST, clear LAST via a .finally() chain. A try/finally inside the async body
  //   clears too early on a synchronous return path (no await before the return): the finally
  //   runs during the call and is then overwritten by this assignment, leaving inflight stuck
  //   on a settled promise — every later call would reuse it and never refresh again.
  const pending = (async () => {
    try {
      // * F2 — cross-tab coordination: a sibling refreshed within the adopt window and
      //   broadcast its token. Adopt instead of POSTing our own refresh — every redundant POST
      //   rotates the shared cookie (multi-tab race) and drains the per-user refresh budget.
      if (store.hasRecentTokenUpdate() && store.accessToken) {
        return {
          accessToken: store.accessToken,
          csrfToken: store.csrfToken,
          expiresIn: store.expiresIn,
        };
      }
      const session = await doRefresh();
      if (session?.accessToken) {
        store.setSessionTokens(session.accessToken, session.csrfToken, session.expiresIn);
        return session;
      }
      // * Rotation race: a sibling rotated the shared cookie while our POST was in flight — the
      //   browser cookie jar now holds the winner's fresh cookie, so retry once with it. A
      //   genuinely dead/revoked session fails both attempts and returns the failure result.
      if (store.hasRecentTokenUpdate()) {
        const retried = await doRefresh();
        if (retried?.accessToken) {
          store.setSessionTokens(retried.accessToken, retried.csrfToken, retried.expiresIn);
          return retried;
        }
        return retried;
      }
      return session;
    } catch {
      return null;
    }
  })();
  inflight = pending;
  return pending.finally(() => {
    if (inflight === pending) {
      inflight = null;
    }
  });
}

// ---------- 401 recovery ----------

export type UnauthorizedOutcome =
  | { action: "pass-through" }
  | { action: "retry"; accessToken: string; csrfToken: string; adopt: boolean }
  | { action: "reject"; status: number; message: string; redirectToLogin: boolean };

// * Handle a 401 for a request. Classifies the endpoint, performs the store effects (refresh
//   adopt / collapse), and returns a decision for the interceptor to execute. The interceptor
//   still owns the retry-key Set, the HTTP retry, and the redirect.
export async function handleUnauthorized(
  store: IdentityStore,
  ctx: { url: string; retryKeySeen: boolean; adoptRetried: boolean },
): Promise<UnauthorizedOutcome> {
  const rotationRaceActive = isRefreshUrl(ctx.url) && store.hasRecentTokenUpdate();
  const kind = classifyUnauthorized({ url: ctx.url, rotationRaceActive });

  if (kind === "session-dead") {
    store.collapseSession();
    return { action: "reject", status: 401, message: "Session expired.", redirectToLogin: true };
  }
  if (kind === "session-race") {
    return {
      action: "reject",
      status: 401,
      message: "Session rotated by another tab.",
      redirectToLogin: false,
    };
  }
  if (kind === "credential") {
    // * Bad credentials — never collapse/broadcast. Fall through to the normalizer so the
    //   caller can surface "invalid credentials".
    return { action: "pass-through" };
  }

  // kind === "other": a generic request that may recover with a fresh token.
  if (gateRefresh({ retryKeySeen: ctx.retryKeySeen }) === "already-retried") {
    return {
      action: "reject",
      status: 401,
      message: "Authorization failed for this request.",
      redirectToLogin: false,
    };
  }

  const session = await ensureSession(store);
  if (session?.accessToken) {
    return {
      action: "retry",
      accessToken: session.accessToken,
      csrfToken: session.csrfToken,
      adopt: false,
    };
  }

  const failure = decideRefreshFailure({
    hasRecentTokenUpdate: store.hasRecentTokenUpdate(),
    adoptRetried: ctx.adoptRetried,
    refreshFailedWith: session?.failedWith,
  });
  if (failure.action === "adopt-retry") {
    return {
      action: "retry",
      accessToken: store.accessToken,
      csrfToken: store.csrfToken,
      adopt: true,
    };
  }
  if (failure.action === "collapse") {
    store.collapseSession();
    return {
      action: "reject",
      status: session?.failedWith ?? 401,
      message: "Session expired. Please log in.",
      redirectToLogin: true,
    };
  }
  // * Transient (429/5xx/network) — leave the session untouched.
  logger.warn("Refresh failed transiently (status {status}); session left untouched.", {
    status: session?.failedWith ?? null,
  });
  return {
    action: "reject",
    status: 401,
    message: "Request failed while the session could not be refreshed.",
    redirectToLogin: false,
  };
}
